import { useApolloClient, useSubscription } from '@apollo/client';
import React, { createContext, FC, ReactNode, useRef, useState } from 'react';

import { useSessionContext } from '../contexts/SessionContext';
import { ExtendedListing } from '../models/Listing';
import { BulkProcessStatus, ListingDocument, ListingQuery } from '../types/api.graphql';
import {
  BulkProcessUpdatedGQL,
  ListingCreatedGQL,
  ListingUpdatedGQL,
  VehicleUpdatedSubscriptionGQL,
} from '../types/subscriptions/listings';

interface ListingsContextProps {
  selectedListing: ExtendedListing | undefined;
  setSelectedListing: React.Dispatch<React.SetStateAction<ExtendedListing | undefined>>;
  scrollableColumnRef: React.MutableRefObject<HTMLDivElement>;
  horizontalScrollBarRef: React.MutableRefObject<HTMLDivElement>;
  isProgrammaticScroll: React.MutableRefObject<boolean>;
  listScrollPosition: number;
  setListScrollPosition: (pos: number) => void;
  isNewListingsDataAvailable: boolean;
  setNewListingsDataAvailable: React.Dispatch<React.SetStateAction<boolean>>;
  globalSearchQuery: string;
  setGlobalSearchQuery: (value: string) => void;
  reloadListings: () => void;
  reloadListingsCallback: React.MutableRefObject<(() => Promise<void>) | null>;
  isBulkOperationLoading: boolean;
  setBulkOperationLoading: (loading: boolean) => void;
}

interface ListingsProviderProps {
  children: ReactNode;
}

const ListingsContext = createContext<ListingsContextProps>(null);

export const ListingsProvider: FC<ListingsProviderProps> = ({ children }) => {
  const { me } = useSessionContext();

  const [selectedListing, setSelectedListing] = useState<ExtendedListing | undefined>(undefined);
  const [listScrollPosition, setListScrollPosition] = useState(0);
  const [isNewListingsDataAvailable, setNewListingsDataAvailable] = useState(false);
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const { cache } = useApolloClient();
  const [isBulkOperationLoading, setBulkOperationLoading] = useState(false);

  // Refs
  const scrollableColumnRef = useRef<HTMLDivElement>(null);
  const horizontalScrollBarRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScroll = useRef(false);
  const reloadListingsCallback = useRef<(() => Promise<void>) | null>(null);

  const reloadListings = () => {
    if (reloadListingsCallback.current) {
      void reloadListingsCallback.current();
    }
  };

  const handleListingSubscription = (response: any) => {
    setNewListingsDataAvailable(true);

    // Handle ListingUpdatedGQL subscription data
    if (response?.data?.listingUpdated) {
      const { id, totalCosts, totalProfit } = response.data.listingUpdated;

      const listingCachedData: ListingQuery = cache.readQuery({
        query: ListingDocument,
        variables: { listingId: id },
      });

      if (listingCachedData?.listing) {
        cache.writeQuery({
          query: ListingDocument,
          variables: { listingId: id },
          data: {
            listing: {
              ...listingCachedData.listing,
              totalCosts,
              totalProfit,
            },
          },
        });
      }
    }
  };

  const handleBulkProcessSubscription = (response: any) => {
    const bulkProcessUpdatedData = response?.data?.data?.bulkProcessUpdated;
    if (bulkProcessUpdatedData) {
      const { status, actor } = bulkProcessUpdatedData;
      const isOwner = actor?.id === me?.id;

      if (status === BulkProcessStatus.Completed) {
        if (isOwner) {
          // Current behavior for operation owner - reload listings and clear loading state
          reloadListings();
        } else {
          // Behavior for non-owners - just show the "new data available" toast
          setNewListingsDataAvailable(true);
        }
      }

      // Clear loading state for owner when process completes or fails
      if (isOwner && status !== BulkProcessStatus.Completed) {
        setBulkOperationLoading(false);
      }
    }
  };

  useSubscription(ListingCreatedGQL, { onData: handleListingSubscription });
  useSubscription(ListingUpdatedGQL, { onData: handleListingSubscription });
  useSubscription(VehicleUpdatedSubscriptionGQL, { onData: handleListingSubscription });
  useSubscription(BulkProcessUpdatedGQL, { onData: handleBulkProcessSubscription });

  return (
    <ListingsContext.Provider
      value={{
        selectedListing,
        setSelectedListing,
        scrollableColumnRef,
        horizontalScrollBarRef,
        isProgrammaticScroll,
        listScrollPosition,
        setListScrollPosition,
        isNewListingsDataAvailable,
        setNewListingsDataAvailable,
        globalSearchQuery,
        setGlobalSearchQuery,
        reloadListings,
        reloadListingsCallback,
        isBulkOperationLoading,
        setBulkOperationLoading,
      }}
    >
      {children}
    </ListingsContext.Provider>
  );
};

export const useListingsContext: () => ListingsContextProps = () => {
  return React.useContext(ListingsContext);
};
